'use client';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import { DateTimePicker } from '@/lib/ui-kit/date-time-picker';
import { Dialog, DialogContent, DialogTitle } from '@/lib/ui-kit/dialog';
import { Text } from '@/lib/ui-kit/text';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/lib/ui-kit/tooltip';
import { Button } from '@/lib/ui-kit-v2/button';

dayjs.extend(utc);

type ExpirationEditProps = {
  value: string | null | undefined; // "24 April 2025,\n15:45 UTC" or null
  onSave: (isoDate: string) => Promise<void>;
  onDelete?: () => Promise<void>;
  disabled?: boolean;
  className?: string;
};

export const ExpirationEdit = ({ value, onSave, disabled, className }: ExpirationEditProps) => {
  const t = useTranslations('expiration');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [isDateTimeValid, setIsDateTimeValid] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const nowDate = useMemo(() => new Date(), []);

  const handleClick = useCallback(() => {
    if (disabled) return;

    let initialDate: Date;
    if (value && value !== '-') {
      if (value === 'End of the day') {
        initialDate = dayjs().utc().endOf('day').toDate();
      } else {
        const parsed = dayjs.utc(value, 'D-MMM-YYYY, HH:mm [UTC]');
        initialDate = parsed.isValid() ? parsed.toDate() : dayjs().utc().toDate();
      }
    } else {
      initialDate = dayjs().utc().toDate();
    }

    setSelectedDate(initialDate);
    setDialogOpen(true);
  }, [value, disabled]);

  const handleConfirm = useCallback(async () => {
    if (!selectedDate || !isDateTimeValid) return;

    setIsSaving(true);
    try {
      const isoString = dayjs(selectedDate).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
      await onSave(isoString);
      setDialogOpen(false);
    } catch (error) {
      console.error('Failed to update expiration:', error);
    } finally {
      setIsSaving(false);
    }
  }, [selectedDate, isDateTimeValid, onSave]);

  const isEmpty = !value || value === '-';

  return (
    <>
      {isEmpty ? (
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          className={`cursor-pointer text-left text-[13px] leading-[1.5] text-v2-text-primary underline
            decoration-v2-text-secondary underline-offset-2 transition-opacity hover:opacity-80
            disabled:cursor-not-allowed disabled:opacity-50 ${className || ''}`}
        >
          {t('add')}
        </button>
      ) : (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleClick}
                disabled={disabled}
                className={`cursor-pointer text-left text-[13px] leading-[1.5] text-v2-text-primary underline
                  decoration-v2-text-secondary underline-offset-2 transition-opacity hover:opacity-80
                  disabled:cursor-not-allowed disabled:opacity-50 md:whitespace-nowrap ${className || ''}`}
              >
                <span className="md:hidden">
                  {value?.includes(', ')
                    ? value.split(', ').map((part, i) => (
                        <span key={i} className="block">
                          {part}
                          {i === 0 && ','}
                        </span>
                      ))
                    : value}
                </span>
                <span className="hidden md:inline">{value}</span>
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <Text variant="body-xs">{t('changeDate')}</Text>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="w-[320px] gap-0 rounded-[10px] border-none bg-v2-surface-primary px-6 pb-4 pt-6 shadow-none"
          showCloseButton={false}
        >
          <DialogTitle className="sr-only">{t('changeExpirationTime')}</DialogTitle>
          <div className="flex flex-col gap-4">
            <DateTimePicker
              value={selectedDate}
              onChange={setSelectedDate}
              minDate={nowDate}
              onValidityChange={setIsDateTimeValid}
            />
            <Button
              appearance="accent"
              size="M"
              onClick={handleConfirm}
              disabled={!isDateTimeValid || !selectedDate || isSaving}
              className="h-10 w-full"
            >
              {isSaving ? t('saving') : t('add')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
